import { Box, Button, IconButton, Link, Typography } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useState } from "react";
import { routes } from "schemas/routes";

import { DISMISSED_NICHE_TEMPLATE_CARD } from "utils/localStorage";
import { trackEvent } from "utils/tracking";

import HomeCard from "../../components/HomeCard";
import { Icon } from "./shared/icons";
import { Callout, Pill } from "./shared/primitives";
import { buttonSx, g } from "./shared/tokens";

interface Props {
  title: string;
  description: string;
  infoBox: string | JSX.Element;
  completed?: boolean;
}

const CreateNicheTemplateCard = ({
  title,
  description,
  infoBox,
  completed,
}: Props) => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setCreateTemplateDialogOpen, outreachTemplates } = useContext(
    OutreachTemplatesContext,
  );

  const dismissKey = `${DISMISSED_NICHE_TEMPLATE_CARD}-${currentOrg?.id}-${title}`;
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(dismissKey) === "true",
  );

  const noTemplates = outreachTemplates.length === 0;

  if (dismissed) {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem(dismissKey, "true");
    setDismissed(true);
  };

  const renderChip = () => {
    if (currentOrg?.isVaUser) {
      return (
        <Pill
          variant={completed ? "default" : "yellow"}
          icon={<Icon.Dollar size={12} />}
        >
          {completed ? "$10 earned" : "Earn $10"}
        </Pill>
      );
    }
    if (currentOrg?.isProUser) {
      return completed ? (
        <Pill variant="default" icon={<Icon.Check size={12} />}>
          Completed
        </Pill>
      ) : (
        <Pill variant="yellow" icon={<Icon.Sparkle size={12} />}>
          Highly recommended
        </Pill>
      );
    }
    return completed ? (
      <Pill variant="default" icon={<Icon.Check size={12} />}>
        50 email credits added
      </Pill>
    ) : (
      <Pill variant="yellow" sx={{ background: "#eed13b" }}>
        +50 email credits
      </Pill>
    );
  };

  return (
    <HomeCard
      icon={completed ? <Icon.Check size={20} /> : <Icon.Doc size={20} />}
      iconSx={{
        background: completed ? g.green100 : g.primaryTint,
        color: completed ? g.successText : g.primary,
      }}
      title="Niche-Specific Template"
      subtitle="Create a niche template to earn a one-time 50 bonus email credits"
      chip={
        <>
          {renderChip()}
          {completed && (
            <IconButton
              size="small"
              aria-label="Dismiss"
              onClick={handleDismiss}
              sx={{ color: g.text2 }}
            >
              <Icon.X size={16} />
            </IconButton>
          )}
        </>
      }
    >
      <Callout tone="yellow" icon={<Icon.Sparkle size={16} />}>
        {infoBox}
      </Callout>

      <Box sx={{ mt: "18px" }}>
        {completed ? (
          <Typography sx={{ fontSize: 14, color: g.text2 }}>
            <Box
              component="span"
              sx={{
                color: g.successText,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                mr: "8px",
                fontWeight: 600,
              }}
            >
              <Icon.Check size={14} /> Niche template created!
            </Box>
            Visit the{" "}
            <Link
              sx={{ fontWeight: 600, textDecoration: "none" }}
              href={`/${routes.templates}`}
            >
              Templates tab
            </Link>{" "}
            to make edits.
          </Typography>
        ) : (
          <>
            <Button
              disabled={noTemplates}
              sx={{ ...buttonSx.pill, ...buttonSx.primary }}
              onClick={() => {
                trackEvent("Gamification - Create Niche Template");
                setCreateTemplateDialogOpen(true);
              }}
            >
              Create niche template{" "}
              {noTemplates ? (
                <Icon.Lock size={14} />
              ) : (
                <Icon.ArrowRight size={14} />
              )}
            </Button>

            {noTemplates && (
              <Typography
                sx={{
                  mt: "12px",
                  fontSize: 13,
                  color: g.textDisabled,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Icon.Lock size={14} />
                <span>
                  Please{" "}
                  <Link
                    sx={{
                      textDecoration: "none",
                      cursor: "pointer",
                      color: g.primary,
                      fontWeight: 700,
                    }}
                    onClick={() => {
                      trackEvent("Gamification - Create First Template");
                      setCreateTemplateDialogOpen(true);
                    }}
                  >
                    create a general template first
                  </Link>{" "}
                  to unlock this challenge.
                </span>
              </Typography>
            )}
          </>
        )}
      </Box>
    </HomeCard>
  );
};

export default CreateNicheTemplateCard;
